<script lang="ts" setup>
import { computed, h, inject, type ComputedRef, type VNode } from 'vue'
import { useI18n } from 'vue-i18n'
import type { feedbackIntegration as FeedbackIntegration } from '@sentry/vue'

import CCard from '@lib/components/CCard/CCard.vue'
import CButton from '@lib/components/CButton/CButton.vue'
import CIcon from '@lib/components/CIcon/CIcon.vue'
import CFeedback from '@lib/components/CFeedback/CFeedback.vue'
import CSelect from '@lib/components/CSelect/CSelect.vue'
import CFileThumbnail from '@lib/components/CFileThumbnail/CFileThumbnail.vue'
import type { PitcherFile } from '@lib/types/file.types'
import type { RecentFile } from '@lib/types/recentFiles.types'
import { FileContentTypeEnum, type File } from '@openapi'
import type { CLIENT_TYPE } from '@lib/constants/cdp.const'
import { MAX_RECENT_FILES_TO_SHOW } from '@lib/constants/recentFiles.const'
import useThemeVars from '@lib/composables/useThemeVars'
import tooltipDirective from '@lib/directives/tooltip'
import type { RenderableFile } from '@lib/types/files'
import type { OrganizationSettings } from '@lib/types/organizationSettings.types'
import type { InstanceSettings } from '@lib/types/instanceSettings.types'
import type { LaunchDarklyEnv } from '@lib/types/launchDarkly.types'
import type { ShareboxItem } from '@lib/components/CFileViewer/CFileViewer.types'

interface Props {
  file: PitcherFile | null
  isPublishing: boolean
  areAnnotationsModified: boolean
  hideActions: boolean
  allowNoteTaking?: boolean
  clientType: (typeof CLIENT_TYPE)[keyof typeof CLIENT_TYPE]
  feedbackIntegration?: typeof FeedbackIntegration
  recentFiles?: RecentFile[]
  track?: (eventName: string, payload: any) => void
  disableFullScreen?: boolean
  hideDownload?: boolean
  isInSharebox?: boolean
  isPreviewMode?: boolean
}

const props = defineProps<Props>()

const emit = defineEmits<{
  closeFile: []
  publishFile: []
  search: []
  downloadFile: []
  toggleNotes: []
  toggleFullscreen: []
  toggleFileInfo: []
  editFile: []
  selectRecentFile: [{ fileId: File['id']; pageIndex?: number }]
  showRecentFiles: [File['id'][]]
  addToSharebox: [ShareboxItem]
}>()

const { t } = useI18n()
const themeVars = useThemeVars()
const vTooltip = tooltipDirective

const pitcherSettings = inject<ComputedRef<OrganizationSettings & InstanceSettings>>(
  'pitcherSettings',
  computed(() => ({})),
)

const launchDarkly = inject<ComputedRef<LaunchDarklyEnv>>(
  'launchDarkly',
  computed(() => ({})),
)
const showEditButton = computed(
  () =>
    !props.file?.isDeleted &&
    props.file?.original_extension &&
    ['pptx'].includes(props.file?.original_extension) &&
    (!launchDarkly?.value?.disable_file_edit_content || props.file?.metadata?.file_edit_url) &&
    (props.clientType === 'admin' || (props.clientType === 'impact' && launchDarkly?.value?.allow_rep_file_upload)),
)
const showPublishButton = computed(
  () =>
    props.file?.content_type === 'pdf' &&
    ((props.clientType === 'admin' && pitcherSettings.value.file_annotations_mode === 'admin') ||
      (props.clientType === 'impact' &&
        pitcherSettings.value.file_annotations_mode === 'personal' &&
        launchDarkly.value.allow_saving_annotations_in_personal_layer)),
)
const showShareboxButton = computed(
  () =>
    props.clientType === 'impact' &&
    !!launchDarkly?.value?.enable_sharebox &&
    !props.hideActions &&
    props.file?.permissions?.can_share === true &&
    !props.isPreviewMode,
)
const recentFilesToShow = computed(() => {
  const result = props.recentFiles?.slice(0, MAX_RECENT_FILES_TO_SHOW) ?? []
  return result?.length && props.file ? [...result, props.file] : result
})

const optionsListMaxHeight = computed(() => {
  if (recentFilesToShow.value.length > 1) {
    const optionsToDisplayCount = recentFilesToShow.value.length - 1
    const headerHeight = 52
    return `${optionsToDisplayCount * 72 + headerHeight}px`
  }
  return '0'
})

const contentTypeLabels: Record<FileContentTypeEnum, string> = {
  [FileContentTypeEnum.AR]: t('canvasUI.contentType.ar'),
  [FileContentTypeEnum.PDF]: t('canvasUI.contentType.pdf'),
  [FileContentTypeEnum.VIDEO]: t('canvasUI.contentType.video'),
  [FileContentTypeEnum.WEB]: t('canvasUI.contentType.web'),
  [FileContentTypeEnum.IMAGE]: t('canvasUI.contentType.image'),
  [FileContentTypeEnum.FONT]: t('canvasUI.contentType.font'),
}

function renderOption(info: { node: VNode; option: RecentFile; selected: boolean }) {
  return info.selected
    ? null
    : h(
        'div',
        {
          class: 'flex flex-col pa-2 max-w-[500px] cursor-pointer',
          onClick: (event: MouseEvent) => {
            processOptionSelection(event, info)
          },
          onTouchend: (event: TouchEvent) => {
            processOptionSelection(event, info)
          },
        },
        [
          h('div', { class: 'flex items-center gap-2' }, [
            h(CFileThumbnail, {
              iconSize: 24,
              item: info.option as RenderableFile,
              width: 73,
              height: 56,
              class: 'flex-shrink-0 h-14 border-rounded-2 overflow-hidden box-border',
            }),
            h('div', { class: 'flex flex-col justify-center truncate' }, [
              h(
                'div',
                {
                  class: 'font-bold truncate',
                },
                info.option.name,
              ),
              h('div', { class: 'flex truncate' }, [
                h(
                  'div',
                  { style: { color: themeVars.value.text3 } },
                  info.option.content_type ? contentTypeLabels[info.option.content_type] : '-',
                ),
              ]),
            ]),
          ]),
        ],
      )
}

function processOptionSelection(event: Event, info: { node: VNode; option: RecentFile; selected: boolean }) {
  event.preventDefault() // Prevent firing click event after touch
  emit('selectRecentFile', { fileId: info.option.id, pageIndex: info.option.page_index })
}

function onToggleRecentFilesSelect(show: boolean) {
  if (!show) return
  const fileIdsToFetch = recentFilesToShow.value?.filter((f) => f.id! !== props.file?.id).map((f) => f.id)
  emit('showRecentFiles', fileIdsToFetch ?? [])
}

function onAddToSharebox() {
  if (!props.file || props.isInSharebox) return
  const shareboxItem: ShareboxItem = {
    id: props.file.id,
    fileId: props.file.id,
    name: props.file.name || '',
    type: 'file' as const,
    thumbnailUrl: props.file.thumbnail_url || undefined,
  }
  emit('addToSharebox', shareboxItem)
}
</script>

<template>
  <CCard :bordered="false" class="px-8 py-4 justify-center c-file-viewer-top h-[76px]">
    <div class="flex justify-between items-center gap-2">
      <div class="flex items-center gap-2">
        <CButton
          v-tooltip="t('canvasUI.components.fileViewer.exit')"
          class="w-11 h-11"
          size="large"
          type="primary"
          @click="emit('closeFile')"
        >
          <template #icon>
            <CIcon icon="close" size="16" />
          </template>
        </CButton>
        <CButton
          v-if="!hideActions"
          v-tooltip="t('canvasUI.components.fileViewer.fileInfoToggle')"
          class="w-11 h-11"
          secondary
          size="large"
          @click="emit('toggleFileInfo')"
        >
          <template #icon>
            <CIcon icon="circle-info" size="16" />
          </template>
        </CButton>
        <CButton
          v-if="!hideDownload"
          v-tooltip="t('canvasUI.components.fileViewer.download')"
          class="w-11 h-11"
          secondary
          size="large"
          @click="emit('downloadFile')"
        >
          <template #icon>
            <CIcon icon="download" size="16" />
          </template>
        </CButton>
        <CButton
          v-if="showShareboxButton"
          v-tooltip="
            props.isInSharebox
              ? t('canvasUI.browserApp.contextMenu.addedToSharebox')
              : t('canvasUI.browserApp.contextMenu.addToSharebox')
          "
          class="w-11 h-11"
          :disabled="props.isInSharebox"
          secondary
          size="large"
          @click="onAddToSharebox"
        >
          <template #icon>
            <CIcon fa-type="fa-kit" icon="regular-share-from-square-circle-plus" size="16" />
          </template>
        </CButton>
      </div>
      <div class="c-file-viewer-top__file-name grid font-normal text-[20px] leading-8 tracking-wide">
        <div
          v-if="recentFilesToShow.length > 1 && file"
          v-tooltip="t('canvasUI.components.fileViewer.recentFiles')"
          class="overflow-hidden max-h-[342px]"
        >
          <CSelect
            class="flex-grow-1 select-none"
            hideDetails
            label-field="name"
            :menuProps="{
              style: {
                '--n-option-height': '72px',
                '--n-option-padding-left': '8px',
                '--n-option-padding-right': '8px',
                transform: 'translateX(-50%)',
                left: '50%',
                'box-shadow': '2px 2px 4px 4px rgba(0, 0, 0, 0.12)',
                maxHeight: optionsListMaxHeight,
              },
              class: 'w-[500px] pa-2',
            }"
            :options="recentFilesToShow"
            placement="bottom-end"
            :renderOption="renderOption"
            to=".c-file-viewer-top"
            :value="file.id"
            value-field="id"
            @update:show="onToggleRecentFilesSelect"
          >
            <template #header>
              <div class="flex items-center gap-1">
                <CIcon icon="clock-rotate-left" />
                <div class="font-bold">
                  {{ t('canvasUI.components.fileViewer.recentlyOpened') }}
                </div>
              </div>
            </template>
          </CSelect>
        </div>
        <div v-else-if="file" class="truncate">{{ file.name }}</div>
      </div>

      <div class="flex items-center gap-2 rd">
        <CButton
          v-if="clientType === 'impact'"
          v-tooltip="t('canvasUI.components.fileViewer.searchForFiles')"
          circle
          class="w-11 h-11"
          secondary
          size="large"
          @click="emit('search')"
        >
          <template #icon>
            <CIcon icon="search" size="16" />
          </template>
        </CButton>
        <CButton
          v-if="clientType === 'impact' && allowNoteTaking"
          v-tooltip="t('canvasUI.components.fileViewer.notesToggle')"
          circle
          class="w-11 h-11"
          secondary
          size="large"
          @click="emit('toggleNotes')"
        >
          <template #icon>
            <CIcon icon="notes" size="16" />
          </template>
        </CButton>
        <CFeedback v-if="feedbackIntegration && track" :feedback-integration="feedbackIntegration" :track="track">
          <CButton circle class="w-11 h-11" secondary size="large">
            <template #icon>
              <CIcon icon="message-exclamation" size="16" />
            </template>
          </CButton>
        </CFeedback>

        <CButton
          v-if="clientType === 'impact' || clientType === 'dsr'"
          v-tooltip="t('canvasUI.components.fileViewer.fullScreen')"
          circle
          class="w-11 h-11"
          :disabled="disableFullScreen"
          secondary
          size="large"
          @click="emit('toggleFullscreen')"
        >
          <template #icon>
            <CIcon fa-type="fas" icon="expand" size="16" />
          </template>
        </CButton>

        <CButton
          v-if="showEditButton"
          v-tooltip="t('canvasUI.file.editContent')"
          circle
          class="w-11 h-11"
          secondary
          size="large"
          @click="emit('editFile')"
        >
          <template #icon>
            <CIcon fa-type="far" icon="file-pen" size="16" />
          </template>
        </CButton>

        <CButton
          v-if="showPublishButton"
          :disabled="!areAnnotationsModified"
          :loading="isPublishing"
          size="large"
          type="primary"
          @click="emit('publishFile')"
        >
          {{
            pitcherSettings.file_annotations_mode === 'admin'
              ? t('canvasUI.components.fileViewer.publish')
              : t('canvasUI.components.fileViewer.save')
          }}
        </CButton>
      </div>
    </div>
  </CCard>
</template>

<style lang="scss" scoped>
.c-file-viewer-top :deep(.n-base-select-menu__header) {
  border-bottom: none;
}

.c-file-viewer-top :deep(.v-vl-items) {
  min-height: auto !important;
}
</style>
